import type {
  IndividualPaymentEditorViewModel,
  IndividualPaymentSaveViewModel,
} from '@/shared/api/generated';

import type { PlanFormValues } from '../hooks/usePlanFormSchema';

/**
 * Campos do `IndividualPaymentSaveViewModel` que a tela **não** expõe e que por
 * isso precisam voltar no `PUT` com o valor que veio do editor.
 *
 * `PUT` é substituição do recurso: omitir um campo provavelmente o zera no
 * backend, então a lista é explícita — e não uma consequência acidental de
 * espalhar o objeto inteiro.
 *
 * `paymentReceiptType`, `totalValueWithoutValueTransfer` e `valueTransfer` estão
 * aqui porque os dois últimos aparecem na tela apenas como leitura.
 *
 * **`appropriateRegister` não está na lista, e não é esquecimento.** Ele existe
 * no `SaveViewModel` (escrita) mas **não** no `IndividualPaymentEditorViewModel`
 * (leitura), então não há de onde copiá-lo: incluí-lo aqui só produziria
 * `undefined`, dando a impressão de uma preservação que nunca aconteceu. Enquanto
 * o backend não devolver o campo no editor, o `PUT` o omite — e omitir é o
 * melhor disponível, já que enviar um valor inventado seria pior.
 */
const PASSTHROUGH_KEYS = [
  'id',
  'courseRegistrationId',
  'courseFinancialId',
  'totalValueWithoutValueTransfer',
  'valueTransfer',
  'paymentReceiptType',
] as const satisfies readonly (keyof IndividualPaymentSaveViewModel)[];

/**
 * Monta o corpo do `PUT` de salvamento do plano.
 *
 * A base é a resposta do editor, e o formulário sobrescreve só o que a tela
 * controla — nunca o contrário. Assim um campo novo que o backend introduza no
 * `SaveViewModel` continua sendo devolvido intacto, mesmo antes de a tela saber
 * que ele existe.
 *
 * @param editor - Resposta de `GET /financial-people/{id}/editor`.
 * @param values - Valores atuais do formulário.
 * @returns Corpo do `PUT`.
 *
 * @example
 * ```ts
 * const payload = buildPlanSavePayload(editor, form.getValues());
 * // payload.appropriateRegister === editor.appropriateRegister
 * ```
 */
export function buildPlanSavePayload(
  editor: IndividualPaymentEditorViewModel | undefined,
  values: PlanFormValues,
): IndividualPaymentSaveViewModel {
  // `personId` é seletor de UI da tela de criação, não campo do `SaveViewModel`.
  // Ele vive no formulário compartilhado, então é retirado aqui antes do envio.
  const { personId: _personId, ...savable } = values;
  const passthrough: Partial<IndividualPaymentSaveViewModel> = {};

  if (editor) {
    for (const key of PASSTHROUGH_KEYS) {
      const value = (editor as Record<string, unknown>)[key];
      if (value !== undefined) {
        (passthrough as Record<string, unknown>)[key] = value;
      }
    }
  }

  return { ...passthrough, ...savable } as IndividualPaymentSaveViewModel;
}
